import { eventBus, EventTypes, type EventPayload } from './EventBus';

export interface EventRecord extends EventPayload {
  id: string;
  sequenceNumber: number;
  storedAt: Date;
  processed: boolean;
  retryCount: number;
  lastRetryAt?: Date;
  errorMessage?: string;
}

export interface EventStoreConfig {
  type: 'postgres' | 'kafka' | 'nats';
  connectionString?: string;
  tableName?: string;
  batchSize: number;
  flushInterval: number;
  retentionDays: number;
  enableCompression: boolean;
}

export interface EventQuery {
  eventType?: string;
  source?: string;
  userId?: string;
  tenantId?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
  limit?: number;
  offset?: number;
  orderBy?: 'timestamp' | 'sequenceNumber';
  orderDirection?: 'asc' | 'desc';
}

export class EventStore {
  private static instance: EventStore;
  private config: EventStoreConfig;
  private eventBuffer: EventRecord[] = [];
  private flushTimer?: NodeJS.Timeout;
  private sequenceCounter = 0;
  private isConnected = false;

  private constructor(config: Partial<EventStoreConfig> = {}) {
    this.config = {
      type: 'postgres',
      tableName: 'event_log',
      batchSize: 100,
      flushInterval: 5000, // 5 seconds
      retentionDays: 90,
      enableCompression: true,
      ...config
    };

    this.initialize();
  }

  static getInstance(config?: Partial<EventStoreConfig>): EventStore {
    if (!EventStore.instance) {
      EventStore.instance = new EventStore(config);
    }
    return EventStore.instance;
  }

  private async initialize(): Promise<void> {
    try {
      await this.connect();
      await this.createTables();
      this.startFlushTimer();
      this.setupEventListeners();
      
      console.log(`[EventStore] Initialized with ${this.config.type} backend`);
    } catch (error) {
      console.error('[EventStore] Initialization failed:', error);
      // Fallback to in-memory storage
      this.fallbackToMemory();
    }
  }

  private async connect(): Promise<void> {
    if (this.config.type === 'postgres') {
      await this.connectPostgres();
    } else if (this.config.type === 'kafka') {
      await this.connectKafka();
    } else if (this.config.type === 'nats') {
      await this.connectNats();
    }
  }

  private async connectPostgres(): Promise<void> {
    // In a real implementation, this would use a PostgreSQL client
    // For now, we'll simulate the connection
    console.log('[EventStore] Connecting to PostgreSQL...');
    
    // Simulate connection delay
    await new Promise(resolve => setTimeout(resolve, 100));
    
    this.isConnected = true;
    console.log('[EventStore] PostgreSQL connection established');
  }

  private async connectKafka(): Promise<void> {
    console.log('[EventStore] Connecting to Kafka...');
    // Kafka connection logic would go here
    this.isConnected = true;
  }

  private async connectNats(): Promise<void> {
    console.log('[EventStore] Connecting to NATS JetStream...');
    // NATS connection logic would go here
    this.isConnected = true;
  }

  private async createTables(): Promise<void> {
    if (this.config.type === 'postgres') {
      const createTableSQL = `
        CREATE TABLE IF NOT EXISTS ${this.config.tableName} (
          id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
          sequence_number BIGSERIAL,
          event_type VARCHAR(255) NOT NULL,
          source VARCHAR(255) NOT NULL,
          user_id VARCHAR(255),
          tenant_id VARCHAR(255),
          data JSONB NOT NULL,
          metadata JSONB,
          timestamp BIGINT NOT NULL,
          stored_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          processed BOOLEAN DEFAULT FALSE,
          retry_count INTEGER DEFAULT 0,
          last_retry_at TIMESTAMP WITH TIME ZONE,
          error_message TEXT,
          INDEX idx_event_type (event_type),
          INDEX idx_source (source),
          INDEX idx_timestamp (timestamp),
          INDEX idx_user_id (user_id),
          INDEX idx_tenant_id (tenant_id),
          INDEX idx_processed (processed)
        );
        
        CREATE INDEX IF NOT EXISTS idx_sequence_number ON ${this.config.tableName} (sequence_number);
        CREATE INDEX IF NOT EXISTS idx_stored_at ON ${this.config.tableName} (stored_at);
      `;
      
      console.log('[EventStore] Creating PostgreSQL tables...');
      // Execute SQL would go here
    }
  }

  private fallbackToMemory(): void {
    console.warn('[EventStore] Falling back to in-memory storage');
    this.isConnected = false;
  }

  private setupEventListeners(): void {
    // Listen to all events from the event bus
    eventBus.subscribe('*', (payload: EventPayload) => {
      this.storeEvent(payload);
    });
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flushBuffer();
    }, this.config.flushInterval);
  }

  // Store event in persistent storage
  async storeEvent(payload: EventPayload): Promise<void> {
    const eventRecord: EventRecord = {
      ...payload,
      id: this.generateId(),
      sequenceNumber: ++this.sequenceCounter,
      storedAt: new Date(),
      processed: false,
      retryCount: 0
    };

    if (this.isConnected) {
      this.eventBuffer.push(eventRecord);
      
      // Flush immediately if buffer is full
      if (this.eventBuffer.length >= this.config.batchSize) {
        await this.flushBuffer();
      }
    } else {
      // Fallback to in-memory storage
      this.storeInMemory(eventRecord);
    }
  }

  private generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private storeInMemory(eventRecord: EventRecord): void {
    // Store in memory as fallback
    this.eventBuffer.push(eventRecord);
    
    // Limit memory usage
    if (this.eventBuffer.length > 10000) {
      this.eventBuffer = this.eventBuffer.slice(-5000);
    }
  }

  private async flushBuffer(): Promise<void> {
    if (this.eventBuffer.length === 0) return;

    const batch = this.eventBuffer.splice(0, this.config.batchSize);
    
    try {
      if (this.config.type === 'postgres') {
        await this.flushToPostgres(batch);
      } else if (this.config.type === 'kafka') {
        await this.flushToKafka(batch);
      } else if (this.config.type === 'nats') {
        await this.flushToNats(batch);
      }
      
      console.log(`[EventStore] Flushed ${batch.length} events to ${this.config.type}`);
    } catch (error) {
      console.error(`[EventStore] Failed to flush events:`, error);
      
      // Put events back in buffer for retry
      this.eventBuffer.unshift(...batch);
      
      // Mark events for retry
      batch.forEach(event => {
        event.retryCount++;
        event.lastRetryAt = new Date();
        event.errorMessage = error instanceof Error ? error.message : 'Unknown error';
      });
    }
  }

  private async flushToPostgres(batch: EventRecord[]): Promise<void> {
    const insertSQL = `
      INSERT INTO ${this.config.tableName} (
        event_type, source, user_id, tenant_id, data, metadata, 
        timestamp, stored_at, processed, retry_count, last_retry_at, error_message
      ) VALUES ${batch.map((_, i) => `($${i * 12 + 1}, $${i * 12 + 2}, $${i * 12 + 3}, $${i * 12 + 4}, $${i * 12 + 5}, $${i * 12 + 6}, $${i * 12 + 7}, $${i * 12 + 8}, $${i * 12 + 9}, $${i * 12 + 10}, $${i * 12 + 11}, $${i * 12 + 12})`).join(', ')}
    `;
    
    const values = batch.flatMap(event => [
      event.eventType,
      event.source,
      event.userId,
      event.tenantId,
      JSON.stringify(event.data),
      JSON.stringify(event.metadata),
      event.timestamp,
      event.storedAt.toISOString(),
      event.processed,
      event.retryCount,
      event.lastRetryAt?.toISOString() || null,
      event.errorMessage || null
    ]);
    
    // Execute SQL would go here
    console.log(`[EventStore] Executing PostgreSQL insert for ${batch.length} events`);
  }

  private async flushToKafka(batch: EventRecord[]): Promise<void> {
    // Kafka producer logic would go here
    console.log(`[EventStore] Sending ${batch.length} events to Kafka`);
  }

  private async flushToNats(batch: EventRecord[]): Promise<void> {
    // NATS JetStream logic would go here
    console.log(`[EventStore] Publishing ${batch.length} events to NATS JetStream`);
  }

  // Query events from persistent storage
  async queryEvents(query: EventQuery): Promise<EventRecord[]> {
    if (!this.isConnected) {
      return this.queryFromMemory(query);
    }

    try {
      if (this.config.type === 'postgres') {
        return await this.queryFromPostgres(query);
      } else if (this.config.type === 'kafka') {
        return await this.queryFromKafka(query);
      } else if (this.config.type === 'nats') {
        return await this.queryFromNats(query);
      }
    } catch (error) {
      console.error('[EventStore] Query failed, falling back to memory:', error);
      return this.queryFromMemory(query);
    }

    return [];
  }

  private async queryFromPostgres(query: EventQuery): Promise<EventRecord[]> {
    let sql = `SELECT * FROM ${this.config.tableName} WHERE 1=1`;
    const params: any[] = [];
    let paramIndex = 1;

    if (query.eventType) {
      sql += ` AND event_type = $${paramIndex++}`;
      params.push(query.eventType);
    }

    if (query.source) {
      sql += ` AND source = $${paramIndex++}`;
      params.push(query.source);
    }

    if (query.userId) {
      sql += ` AND user_id = $${paramIndex++}`;
      params.push(query.userId);
    }

    if (query.tenantId) {
      sql += ` AND tenant_id = $${paramIndex++}`;
      params.push(query.tenantId);
    }

    if (query.dateRange) {
      sql += ` AND timestamp BETWEEN $${paramIndex++} AND $${paramIndex++}`;
      params.push(query.dateRange.start.getTime(), query.dateRange.end.getTime());
    }

    const orderBy = query.orderBy || 'timestamp';
    const orderDirection = query.orderDirection || 'desc';
    sql += ` ORDER BY ${orderBy} ${orderDirection.toUpperCase()}`;

    if (query.limit) {
      sql += ` LIMIT $${paramIndex++}`;
      params.push(query.limit);
    }

    if (query.offset) {
      sql += ` OFFSET $${paramIndex++}`;
      params.push(query.offset);
    }

    console.log('[EventStore] Executing PostgreSQL query:', sql);
    // Execute SQL and return results would go here
    
    // Return mock data for now
    return [];
  }

  private queryFromMemory(query: EventQuery): EventRecord[] {
    let results = [...this.eventBuffer];

    if (query.eventType) {
      results = results.filter(event => event.eventType === query.eventType);
    }

    if (query.source) {
      results = results.filter(event => event.source === query.source);
    }

    if (query.userId) {
      results = results.filter(event => event.userId === query.userId);
    }

    if (query.tenantId) {
      results = results.filter(event => event.tenantId === query.tenantId);
    }

    if (query.dateRange) {
      results = results.filter(event => 
        event.timestamp >= query.dateRange!.start.getTime() &&
        event.timestamp <= query.dateRange!.end.getTime()
      );
    }

    const orderBy = query.orderBy || 'timestamp';
    const orderDirection = query.orderDirection || 'desc';
    
    results.sort((a, b) => {
      let comparison = 0;
      if (orderBy === 'timestamp') {
        comparison = a.timestamp - b.timestamp;
      } else if (orderBy === 'sequenceNumber') {
        comparison = a.sequenceNumber - b.sequenceNumber;
      }
      return orderDirection === 'desc' ? -comparison : comparison;
    });

    if (query.limit) {
      results = results.slice(0, query.limit);
    }

    if (query.offset) {
      results = results.slice(query.offset);
    }

    return results;
  }

  private async queryFromKafka(query: EventQuery): Promise<EventRecord[]> {
    // Kafka consumer logic would go here
    return [];
  }

  private async queryFromNats(query: EventQuery): Promise<EventRecord[]> {
    // NATS JetStream consumer logic would go here
    return [];
  }

  // Get event statistics
  async getEventStats(): Promise<Record<string, any>> {
    if (!this.isConnected) {
      return this.getMemoryStats();
    }

    try {
      if (this.config.type === 'postgres') {
        return await this.getPostgresStats();
      }
    } catch (error) {
      console.error('[EventStore] Failed to get stats, using memory stats:', error);
      return this.getMemoryStats();
    }

    return {};
  }

  private getMemoryStats(): Record<string, any> {
    const now = Date.now();
    const oneDayAgo = now - (24 * 60 * 60 * 1000);
    const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000);

    const events24h = this.eventBuffer.filter(e => e.timestamp >= oneDayAgo).length;
    const events7d = this.eventBuffer.filter(e => e.timestamp >= oneWeekAgo).length;

    return {
      totalEvents: this.eventBuffer.length,
      events24h,
      events7d,
      bufferSize: this.eventBuffer.length,
      oldestEvent: this.eventBuffer[0]?.timestamp,
      newestEvent: this.eventBuffer[this.eventBuffer.length - 1]?.timestamp
    };
  }

  private async getPostgresStats(): Promise<Record<string, any>> {
    const statsSQL = `
      SELECT 
        COUNT(*) as total_events,
        COUNT(*) FILTER (WHERE stored_at >= NOW() - INTERVAL '24 hours') as events_24h,
        COUNT(*) FILTER (WHERE stored_at >= NOW() - INTERVAL '7 days') as events_7d,
        COUNT(*) FILTER (WHERE processed = false) as unprocessed_events,
        COUNT(*) FILTER (WHERE retry_count > 0) as failed_events,
        MIN(timestamp) as oldest_event,
        MAX(timestamp) as newest_event
      FROM ${this.config.tableName}
    `;

    // Execute SQL and return results would go here
    return {};
  }

  // Clean up old events
  async cleanupOldEvents(): Promise<void> {
    if (!this.isConnected) return;

    try {
      if (this.config.type === 'postgres') {
        const cleanupSQL = `
          DELETE FROM ${this.config.tableName} 
          WHERE stored_at < NOW() - INTERVAL '${this.config.retentionDays} days'
        `;
        
        console.log(`[EventStore] Cleaning up events older than ${this.config.retentionDays} days`);
        // Execute cleanup SQL would go here
      }
    } catch (error) {
      console.error('[EventStore] Cleanup failed:', error);
    }
  }

  // Mark events as processed
  async markEventsProcessed(eventIds: string[]): Promise<void> {
    if (!this.isConnected || eventIds.length === 0) return;

    try {
      if (this.config.type === 'postgres') {
        const updateSQL = `
          UPDATE ${this.config.tableName} 
          SET processed = true, last_retry_at = NOW() 
          WHERE id = ANY($1)
        `;
        
        console.log(`[EventStore] Marking ${eventIds.length} events as processed`);
        // Execute update SQL would go here
      }
    } catch (error) {
      console.error('[EventStore] Failed to mark events as processed:', error);
    }
  }

  // Get connection status
  getConnectionStatus(): { connected: boolean; type: string; lastError?: string } {
    return {
      connected: this.isConnected,
      type: this.config.type,
      lastError: this.eventBuffer.find(e => e.errorMessage)?.errorMessage
    };
  }

  // Update configuration
  updateConfig(updates: Partial<EventStoreConfig>): void {
    this.config = { ...this.config, ...updates };
    
    // Restart with new configuration
    this.restart();
  }

  private async restart(): Promise<void> {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    
    this.eventBuffer = [];
    this.sequenceCounter = 0;
    this.isConnected = false;
    
    await this.initialize();
  }

  // Cleanup
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    
    this.flushBuffer(); // Final flush
    console.log('[EventStore] Destroyed');
  }
}

// Export singleton instance
export const eventStore = EventStore.getInstance();

// Export types
export type { EventRecord, EventQuery, EventStoreConfig };